import { useEffect, useState, type ComponentProps } from 'react';
import { motion } from 'framer-motion';
import {
  Download,
  MessageSquare,
  ChevronRight,
  Phone,
  MapPin,
  CalendarPlus,
  Play,
  FileText,
  ChevronDown,
  ExternalLink,
  Star,
  Navigation,
  Map as MapIcon,
  ShoppingBag,
  Plus,
  Minus,
} from 'lucide-react';
import { getTheme, isLightColor } from '@/lib/themes';
import { sectionConfig, type SectionType } from '@/lib/sectionTypes';
import { linkMeta, linkHref } from '@/lib/linkTypes';
import { parsePaymentConfig, enabledPaymentMethods, shopEnabled, type PaymentMethod } from '@/lib/payments';
import { productFromLink, type Product } from '@/lib/products';
import { formatMoney } from '@/lib/money';
import { resolveBlockOrder, sectionIdFromKey, BIO_KEY, EVENTS_KEY, LINKS_KEY } from '@/lib/layout';
import { CartProvider, useCart } from './cart';
import { CheckoutSheet } from './CheckoutSheet';
import { ProductSheet } from './ProductSheet';
import { listContainer, listChild, SPRING_DEFAULT } from '@/lib/motion';
import { youtubeEmbed, youtubeThumb, isVimeo, vimeoEmbed, isUploadedVideo } from '@/lib/video';
import { fileTypeFromUrl, prettyFileLabel } from '@/lib/files';
import { detectEmbed } from '@/lib/embeds';
import { detectBooking } from '@/lib/booking';
import { mapsLinkFor, mapsEmbedFor, directionsLinkFor } from '@/lib/maps';
import { locationFromLink } from '@/lib/places';
import { recurrenceLabel } from '@/lib/ics';
import { cn } from '@/lib/utils';
import { CardHero } from './CardHero';
import { VideoModal } from './VideoModal';
import { MediaLightbox } from './MediaLightbox';
import { WalletButtons } from './WalletButtons';
import type { Card, CardLink, CardLinkSection, EventRow } from '@/types/database';

export interface PublicCardLayoutProps {
  card: Pick<
    Card,
    | 'id'
    | 'slug'
    | 'display_name'
    | 'title'
    | 'company'
    | 'bio'
    | 'photo_url'
    | 'cover_image_url'
    | 'company_logo_url'
    | 'theme_preset'
    | 'custom_accent'
    | 'custom_bg'
    | 'custom_card_bg'
    | 'custom_text'
    | 'exchange_phone'
    | 'primary_cta_label'
    | 'primary_cta_url'
    | 'secondary_cta_label'
    | 'secondary_cta_url'
  > &
    Partial<Pick<Card, 'payment_config' | 'layout_order'>>;
  links: CardLink[];
  sections: CardLinkSection[];
  events?: EventRow[];
  onSaveContact?: () => void;
  onExchangeContact?: () => void;
  onLinkTap?: (link: CardLink) => void;
  onAddEventToCalendar?: (event: EventRow) => void;
  /** Analytics callback fired when a visitor shares the card onward. */
  onShare?: () => void;
  /** Shop funnel analytics. */
  onAddToCart?: (product: Product) => void;
  onCheckoutStart?: () => void;
  onOrderPlaced?: (order: { id: string; method: PaymentMethod; amountCents: number }) => void;
  onPaymentClaimed?: (orderId: string) => void;
  ownerBackHref?: string;
  preview?: boolean;
  compact?: boolean;
}

export function PublicCardLayout({
  card,
  links,
  sections,
  events,
  onSaveContact,
  onExchangeContact,
  onLinkTap,
  onAddEventToCalendar,
  onShare,
  onAddToCart,
  onCheckoutStart,
  onOrderPlaced,
  onPaymentClaimed,
  ownerBackHref,
  preview = false,
  compact = false,
}: PublicCardLayoutProps) {
  const theme = getTheme(card.theme_preset, card);
  const isLightCard = isLightColor(theme.cardBg);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const linksBySection: Record<string, CardLink[]> = {};
  for (const l of links) {
    const key = l.section_id ?? '__none__';
    (linksBySection[key] ??= []).push(l);
  }

  // Shop: every product across every products section feeds one cart.
  // Visitors only see published products, and only when the shop switch is
  // on; the editor preview shows everything (hidden items badged) so the
  // owner can see what they're building.
  const paymentConfig = parsePaymentConfig(card.payment_config);
  const shopOn = shopEnabled(paymentConfig);
  const canOrder = shopOn && enabledPaymentMethods(paymentConfig).length > 0;
  const products: Product[] = sections
    .filter((s) => s.type === 'products')
    .flatMap((s) => (linksBySection[s.id] ?? []).map(productFromLink))
    .filter((p) => preview || p.published);
  const hasShop = shopOn && products.some((p) => p.published);

  // Page blocks in the owner's chosen order (bio / events / sections / links).
  const order = resolveBlockOrder(card.layout_order, sections);
  const blocks: React.ReactNode[] = [];
  for (const key of order) {
    if (key === BIO_KEY) {
      if (!card.bio) continue;
      blocks.push(
        <PageSection key="bio" compact={compact}>
          <SectionLabel themeText={theme.text}>About</SectionLabel>
          <p
            className="mt-3 text-balance text-sm leading-relaxed"
            style={{ color: theme.text, opacity: 0.85 }}
          >
            {card.bio}
          </p>
        </PageSection>,
      );
      continue;
    }
    if (key === EVENTS_KEY) {
      if (!events || events.length === 0) continue;
      blocks.push(
        <EventsBlock
          key="events"
          events={events}
          theme={theme}
          isLightCard={isLightCard}
          compact={compact}
          preview={preview}
          onAddEventToCalendar={onAddEventToCalendar}
        />,
      );
      continue;
    }
    if (key === LINKS_KEY) {
      const loose = linksBySection['__none__'] ?? [];
      if (loose.length === 0) continue;
      blocks.push(
        <PageSection key="links" compact={compact}>
          <SectionLabel themeText={theme.text}>Links</SectionLabel>
          <SectionRenderer
            section={{
              id: '__none__',
              card_id: card.id,
              name: 'Links',
              type: 'links',
              sort_order: 999,
              created_at: '',
            }}
            items={loose}
            theme={theme}
            onLinkTap={onLinkTap}
            preview={preview}
            canOrder={canOrder}
          />
        </PageSection>,
      );
      continue;
    }
    const sid = sectionIdFromKey(key);
    const s = sid ? sections.find((x) => x.id === sid) : undefined;
    if (!s) continue;
    let items = linksBySection[s.id] ?? [];
    if (s.type === 'products') {
      if (!preview) {
        if (!shopOn) continue;
        items = items.filter((l) => productFromLink(l).published);
      }
    }
    if (items.length === 0) continue;
    blocks.push(
      <PageSection key={s.id} compact={compact}>
        <SectionLabel themeText={theme.text}>{s.name}</SectionLabel>
        <SectionRenderer
          section={s}
          items={items}
          theme={theme}
          onLinkTap={onLinkTap}
          preview={preview}
          canOrder={canOrder}
          shopOn={shopOn}
        />
      </PageSection>,
    );
  }

  return (
    <CartProvider cardId={card.id} products={products} persist={!preview} onAdd={onAddToCart}>
    <div
      className="relative min-h-full"
      style={{ backgroundColor: theme.pageBg, color: theme.text }}
    >
      {/* Cover banner + floating profile card — frame-specific layout */}
      <CardHero
        card={card}
        theme={theme}
        preview={preview}
        ownerBackHref={ownerBackHref}
        compact={compact}
      />

      {/* Wallet passes + CTAs sit below the floating card, regardless of frame */}
      <div className="relative z-10 mx-auto max-w-md px-5">
        {(ownerBackHref || preview) && (
          <div className="mt-3">
            <WalletButtons
              isLightCard={isLightCard}
              textColor={theme.text}
              cardBg={theme.cardBg}
              cardId={card.id}
              interactive={!preview}
            />
          </div>
        )}

        {/* Owner-defined CTAs render ABOVE the default Save/Exchange row.
            Each pair (label + url) renders only when both halves are set. */}
        <OwnerCtas
          card={card}
          theme={theme}
          isLightCard={isLightCard}
          preview={preview}
        />

        {/* Default CTAs — Save contact + Exchange contact. */}
        <div className="public-contact-actions mt-2 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 145px), 1fr))' }}>
          <button
            onClick={onSaveContact}
            disabled={preview}
            className="flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full border px-3 py-3 text-xs font-medium tracking-tight transition-transform active:scale-[0.98] disabled:opacity-80"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: isLightCard ? 'rgba(10,10,10,0.18)' : 'rgba(255,255,255,0.18)',
              color: theme.text,
            }}
          >
            <Download className="h-3.5 w-3.5 shrink-0" />
            Save to phone
          </button>
          <button
            onClick={onExchangeContact}
            disabled={preview}
            className="flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 py-3 text-xs font-medium tracking-tight transition-transform active:scale-[0.98] disabled:opacity-80"
            style={{ backgroundColor: theme.text, color: theme.cardBg }}
          >
            <MessageSquare className="h-3.5 w-3.5 shrink-0" />
            Exchange contact
          </button>
        </div>
      </div>

      {/* Page blocks — About, events, typed sections, loose links — in the
          owner's chosen order (cards.layout_order). */}
      {blocks.length > 0 && <div className="space-y-7 pt-7">{blocks}</div>}

      {/* Sticky order bar — only when the card sells something. */}
      {hasShop && (
        <CartBar
          theme={theme}
          isLightCard={isLightCard}
          currency={(paymentConfig.currency ?? 'USD').toUpperCase()}
          preview={preview}
          canOrder={canOrder}
          onCheckout={() => setCheckoutOpen(true)}
        />
      )}

      <footer className="mx-auto mt-12 max-w-md px-5 pb-8">
        {!preview && (
          <ShareCardButton
            slug={card.slug}
            name={card.display_name}
            theme={theme}
            isLightCard={isLightCard}
            onShare={onShare}
          />
        )}
        <div className="mt-6 flex items-center justify-center gap-1.5 opacity-50">
          <span
            className="font-mono text-[9px] uppercase tracking-widest"
            style={{ color: theme.text }}
          >
            Powered by Haphe
          </span>
        </div>
      </footer>
    </div>

      {hasShop && !preview && (
        <CheckoutSheet
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          card={{ id: card.id, display_name: card.display_name, slug: card.slug }}
          config={paymentConfig}
          theme={theme}
          onCheckoutStart={onCheckoutStart}
          onOrderPlaced={onOrderPlaced}
          onPaymentClaimed={onPaymentClaimed}
        />
      )}
    </CartProvider>
  );
}

// ─── Events block (Add to Calendar) ──────────────────────────────────────────
function EventsBlock({
  events,
  theme,
  isLightCard,
  compact,
  preview,
  onAddEventToCalendar,
}: {
  events: EventRow[];
  theme: ReturnType<typeof getTheme>;
  isLightCard: boolean;
  compact?: boolean;
  preview?: boolean;
  onAddEventToCalendar?: (event: EventRow) => void;
}) {
  return (
      <PageSection compact={compact} className="pt-6">
        <SectionLabel themeText={theme.text}>Upcoming</SectionLabel>
        <ul className="mt-3 space-y-2">
          {events.map((ev) => {
            const repeats = recurrenceLabel(ev.recurrence, ev.starts_at);
            return (
              <li
                key={ev.id}
                className="overflow-hidden rounded-xl border"
                style={{
                  backgroundColor: theme.cardBg,
                  borderColor: isLightCard ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)',
                }}
              >
                <div className="flex items-stretch gap-0">
                  {ev.location && (
                    <a
                      href={preview ? undefined : mapsLinkFor(ev.location)}
                      target={preview ? undefined : '_blank'}
                      rel="noreferrer noopener"
                      onClick={(e) => preview && e.preventDefault()}
                      className="relative w-[35%] shrink-0 overflow-hidden border-r"
                      style={{
                        borderColor: isLightCard
                          ? 'rgba(10,10,10,0.08)'
                          : 'rgba(255,255,255,0.08)',
                        minHeight: 120,
                      }}
                      aria-label={`Open ${ev.location} in Maps`}
                    >
                      <iframe
                        src={mapsEmbedFor(ev.location)}
                        title={`Map of ${ev.location}`}
                        className="pointer-events-none absolute inset-0 h-full w-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                      <span
                        className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-ink/70 px-2 py-0.5 text-[9px] font-medium uppercase tracking-widest text-bone backdrop-blur-md"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                        Open
                      </span>
                    </a>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col p-4">
                    {/* Date pill above the title — denser stack against the map */}
                    <p
                      className="font-mono text-[10px] font-medium uppercase tracking-[0.18em]"
                      style={{ color: theme.accent }}
                    >
                      {repeats
                        ? `${repeats.toUpperCase()} · ${new Date(ev.starts_at).toLocaleString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}`
                        : new Date(ev.starts_at)
                            .toLocaleString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                            .toUpperCase()}
                    </p>

                    <h3
                      className="mt-1.5 text-base font-medium leading-tight tracking-tight"
                      style={{ color: theme.text }}
                    >
                      {ev.name}
                    </h3>

                    {ev.location && (
                      <p
                        className="mt-1.5 truncate text-xs"
                        style={{ color: theme.text, opacity: 0.7 }}
                      >
                        {ev.location}
                      </p>
                    )}

                    {ev.description && (
                      <p
                        className="mt-2 line-clamp-3 text-xs leading-relaxed"
                        style={{ color: theme.text, opacity: 0.75 }}
                      >
                        {ev.description}
                      </p>
                    )}

                    <div className="mt-auto flex justify-end pt-3">
                      <button
                        onClick={() => !preview && onAddEventToCalendar?.(ev)}
                        disabled={preview}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium tracking-tight transition-transform active:scale-[0.98]"
                        style={{ backgroundColor: theme.accent, color: theme.cardBg }}
                      >
                        <CalendarPlus className="h-3 w-3" />
                        Add to Calendar
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </PageSection>
  );
}

// ─── Sticky cart bar ─────────────────────────────────────────────────────────
function CartBar({
  theme,
  isLightCard,
  currency,
  preview,
  canOrder,
  onCheckout,
}: {
  theme: ReturnType<typeof getTheme>;
  isLightCard: boolean;
  currency: string;
  preview?: boolean;
  canOrder: boolean;
  onCheckout: () => void;
}) {
  const cart = useCart();
  if (!cart || cart.count === 0) return null;
  return (
    <div className="sticky bottom-0 z-20 mx-auto max-w-md px-4 pb-4 pt-3 safe-pb">
      <motion.button
        type="button"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={SPRING_DEFAULT}
        onClick={() => !preview && onCheckout()}
        disabled={preview || !canOrder}
        className="flex w-full items-center justify-between gap-3 rounded-full border px-4 py-3 text-left shadow-[0_18px_40px_-20px_rgba(10,10,10,0.45)] backdrop-blur-md transition-transform active:scale-[0.98] disabled:opacity-90"
        style={{
          backgroundColor: theme.text,
          color: theme.cardBg,
          borderColor: isLightCard ? 'rgba(255,255,255,0.12)' : 'rgba(10,10,10,0.12)',
        }}
      >
        <span className="flex items-center gap-2.5">
          <span
            className="relative flex h-8 w-8 items-center justify-center rounded-full"
            style={{ backgroundColor: theme.cardBg + '22' }}
          >
            <ShoppingBag className="h-4 w-4" />
            <span
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-mono text-[9px] font-medium"
              style={{ backgroundColor: theme.accent, color: theme.cardBg }}
            >
              {cart.count}
            </span>
          </span>
          <span className="text-sm font-medium tracking-tight">
            {cart.count} {cart.count === 1 ? 'item' : 'items'} · {formatMoney(cart.subtotal, currency)}
          </span>
        </span>
        <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-widest">
          {canOrder ? 'Checkout' : 'Ordering soon'}
          {canOrder && <ChevronRight className="h-3.5 w-3.5" />}
        </span>
      </motion.button>
    </div>
  );
}

/**
 * Visitor-facing "share this card onward" affordance — native share sheet
 * where available, copy-link everywhere else. This is the growth loop: a
 * visitor who wants to pass the card along shouldn't have to copy the URL
 * from the address bar.
 */
function ShareCardButton({
  slug,
  name,
  theme,
  isLightCard,
  onShare,
}: {
  slug: string;
  name: string;
  theme: ReturnType<typeof getTheme>;
  isLightCard: boolean;
  onShare?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function share() {
    const url = `${window.location.origin}/u/${slug}`;
    onShare?.();
    if (navigator.share) {
      try {
        await navigator.share({ title: name, text: `${name}'s card on Haphe`, url });
        return;
      } catch {
        /* dismissed or unsupported — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — nothing else to try */
    }
  }

  return (
    <button
      onClick={share}
      className="mx-auto flex items-center justify-center gap-1.5 rounded-full border px-4 py-2 text-[11px] font-medium tracking-tight transition-transform active:scale-[0.98]"
      style={{
        backgroundColor: theme.cardBg,
        borderColor: isLightCard ? 'rgba(10,10,10,0.14)' : 'rgba(255,255,255,0.14)',
        color: theme.text,
      }}
    >
      <ExternalLink className="h-3 w-3" />
      {copied ? 'Link copied' : 'Share this card'}
    </button>
  );
}

// ─── Owner CTAs (primary + secondary) ────────────────────────────────────────
function OwnerCtas({
  card,
  theme,
  isLightCard,
  preview,
}: {
  card: PublicCardLayoutProps['card'];
  theme: ReturnType<typeof getTheme>;
  isLightCard: boolean;
  preview?: boolean;
}) {
  const primary =
    card.primary_cta_label && card.primary_cta_url
      ? { label: card.primary_cta_label, url: card.primary_cta_url }
      : null;
  const secondary =
    card.secondary_cta_label && card.secondary_cta_url
      ? { label: card.secondary_cta_label, url: card.secondary_cta_url }
      : null;

  if (!primary && !secondary) return null;

  const primaryStyle: React.CSSProperties = {
    backgroundColor: theme.accent,
    color: theme.cardBg,
  };
  const secondaryStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    borderColor: theme.accent,
    color: theme.text,
  };

  // Layout: one button = full width. Two = side-by-side on sm+, stacked on mobile.
  const both = primary && secondary;
  return (
    <div
      className={cn(
        'mt-2 grid gap-2',
        both ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
      )}
    >
      {primary && (
        <CtaButton
          href={ctaHref(primary.url)}
          label={primary.label}
          style={primaryStyle}
          preview={preview}
          variant="filled"
          isLightCard={isLightCard}
        />
      )}
      {secondary && (
        <CtaButton
          href={ctaHref(secondary.url)}
          label={secondary.label}
          style={secondaryStyle}
          preview={preview}
          variant="outlined"
          isLightCard={isLightCard}
        />
      )}
    </div>
  );
}

function CtaButton({
  href,
  label,
  style,
  preview,
  variant,
  isLightCard,
}: {
  href: string;
  label: string;
  style: React.CSSProperties;
  preview?: boolean;
  variant: 'filled' | 'outlined';
  isLightCard: boolean;
}) {
  const className = cn(
    'flex items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-medium tracking-tight transition-transform active:scale-[0.98]',
    variant === 'outlined' && 'border',
  );
  if (preview) {
    return (
      <span className={cn(className, 'cursor-default opacity-80')} style={style}>
        {label}
      </span>
    );
  }
  return (
    <a
      href={href}
      target={/^(https?:)/.test(href) ? '_blank' : undefined}
      rel="noreferrer noopener"
      className={className}
      style={style}
      data-light-card={isLightCard ? 'true' : 'false'}
    >
      {label}
    </a>
  );
}

/** Normalize a user-entered CTA value into a usable href. */
function ctaHref(v: string): string {
  const t = v.trim();
  if (!t) return '#';
  if (/^(https?:|mailto:|tel:|sms:|#|\/)/i.test(t)) return t;
  return `https://${t}`;
}

// ─── Layout primitives ────────────────────────────────────────────────────────
function PageSection({
  children,
  className,
  compact,
}: {
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <section className={cn('mx-auto max-w-md', compact ? 'px-4' : 'px-5', className)}>
      {children}
    </section>
  );
}

function SectionLabel({ children, themeText }: { children: React.ReactNode; themeText: string }) {
  return (
    <p
      className="font-mono text-[10px] uppercase tracking-[0.22em]"
      // 0.75 (not 0.6) — labels at 60% opacity dipped below WCAG AA on the
      // darker themes (blueprint, neon, mono).
      style={{ color: themeText, opacity: 0.75 }}
    >
      {children}
    </p>
  );
}

// ─── Per-section renderer ────────────────────────────────────────────────────
function SectionRenderer({
  section,
  items,
  theme,
  onLinkTap,
  preview,
  canOrder,
  shopOn,
}: {
  section: CardLinkSection;
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  onLinkTap?: (link: CardLink) => void;
  preview?: boolean;
  canOrder?: boolean;
  shopOn?: boolean;
}) {
  const cfg = sectionConfig(section.type as SectionType);

  switch (cfg.renderStyle) {
    case 'product-grid':
      return (
        <ProductsSection
          items={items}
          theme={theme}
          preview={preview}
          canOrder={!!canOrder}
          shopOn={!!shopOn}
        />
      );
    case 'icon-row':
      return <SocialIconRow items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'video-cards':
      return <VideoCarousel items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'photo-grid':
      return <GalleryCarousel items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'file-list':
      return <FileGrid items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'faq-list':
      return <FaqList items={items} theme={theme} />;
    case 'booking-embed':
      return <BookingSection items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'location-cards':
      return <LocationsSection items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
    case 'link-list':
    default:
      return <LinkList items={items} theme={theme} preview={preview} onTap={onLinkTap} />;
  }
}

// ─── Booking embed (Calendly / Square / etc.) ────────────────────────────────
function BookingSection({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  const isLight = isLightColor(theme.cardBg);
  return (
    <motion.div
      variants={listContainer}
      initial="hidden"
      animate="show"
      className="mt-3 space-y-4"
    >
      {items.map((link) => {
        const match = detectBooking(link.value);
        return (
          <motion.div
            key={link.id}
            variants={listChild}
            className="overflow-hidden rounded-xl border"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)',
            }}
          >
            {/* Header: title + provider pill */}
            <div className="flex items-start justify-between gap-3 px-4 pt-3.5">
              <div className="min-w-0">
                {link.label && (
                  <p
                    className="truncate text-sm font-medium tracking-tight"
                    style={{ color: theme.text }}
                  >
                    {link.label}
                  </p>
                )}
                {link.description && (
                  <p
                    className="mt-0.5 line-clamp-2 text-xs leading-relaxed"
                    style={{ color: theme.text, opacity: 0.7 }}
                  >
                    {link.description}
                  </p>
                )}
              </div>
              {match && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                  style={{ backgroundColor: match.provider.color + '22', color: match.provider.color }}
                >
                  {match.provider.name}
                </span>
              )}
            </div>

            {/* Embed (or fallback button) */}
            {match && match.embedSrc && !preview ? (
              <div className="mt-3 w-full overflow-hidden bg-white">
                <EmbedFrame
                  src={match.embedSrc}
                  title={link.label || `Booking · ${match.provider.name}`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allow="payment; microphone; camera"
                  className="block h-[680px] w-full border-0"
                />
              </div>
            ) : (
              <div className="px-4 pb-4 pt-3">
                <a
                  href={preview ? undefined : (match?.url ?? link.value)}
                  target={preview ? undefined : '_blank'}
                  rel="noreferrer noopener"
                  onClick={(e) => {
                    if (preview) {
                      e.preventDefault();
                      return;
                    }
                    onTap?.(link);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-xs font-medium tracking-tight transition-transform active:scale-[0.98]"
                  style={{ backgroundColor: theme.accent, color: theme.cardBg }}
                >
                  <CalendarPlus className="h-3.5 w-3.5" />
                  Book now
                </a>
              </div>
            )}

            {/* Always offer an "Open in new tab" affordance under the embed */}
            {match && match.embedSrc && !preview && (
              <div className="flex items-center justify-end px-4 py-2">
                <a
                  href={match.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => onTap?.(link)}
                  className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest opacity-60 transition-opacity hover:opacity-100"
                  style={{ color: theme.text }}
                >
                  <ExternalLink className="h-2.5 w-2.5" />
                  Open booking page
                </a>
              </div>
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
}

// ─── Locations (business map + rating + directions) ──────────────────────────
function LocationsSection({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  const isLight = isLightColor(theme.cardBg);
  const border = isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)';

  // Only show locations that have actually been resolved (have an address).
  // A blank, half-added item shouldn't render a placeholder card publicly.
  const resolved = items.filter((l) => locationFromLink(l).address);
  if (resolved.length === 0) return null;

  return (
    <motion.ul
      variants={listContainer}
      initial="hidden"
      animate="show"
      className="mt-3 space-y-3"
    >
      {resolved.map((link) => {
        const loc = locationFromLink(link);
        const mapTarget = loc.address || loc.name;
        // The owner's canonical Google business page when we have it; otherwise
        // a search deep link built from the address.
        const googleHref = loc.googleMapsUri || mapsLinkFor(mapTarget);
        const directionsHref = directionsLinkFor(mapTarget);

        const pill =
          'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium tracking-tight transition-transform active:scale-[0.98]';
        const iconBtn =
          'inline-flex h-7 w-7 items-center justify-center rounded-full border transition-transform active:scale-[0.95]';

        return (
          <motion.li
            key={link.id}
            variants={listChild}
            className="rounded-xl border p-3.5"
            style={{ backgroundColor: theme.cardBg, borderColor: border }}
          >
            {/* Header: name + address on the left, compact rating badge right */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3
                  className="text-sm font-medium leading-snug tracking-tight"
                  style={{ color: theme.text }}
                >
                  {loc.name}
                </h3>
                {loc.address && (
                  <p
                    className="mt-1 flex items-start gap-1 text-xs leading-snug"
                    style={{ color: theme.text, opacity: 0.6 }}
                  >
                    <MapPin className="mt-[1px] h-3 w-3 shrink-0" />
                    <span className="line-clamp-1">{loc.address}</span>
                  </p>
                )}
              </div>
              {loc.rating != null && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5"
                  style={{ backgroundColor: theme.accent + '14' }}
                  aria-label={`${loc.rating.toFixed(1)} out of 5`}
                >
                  <Star className="h-3 w-3 fill-current" style={{ color: theme.accent }} />
                  <span
                    className="text-xs font-semibold tabular-nums"
                    style={{ color: theme.text }}
                  >
                    {loc.rating.toFixed(1)}
                  </span>
                  {loc.reviewCount != null && (
                    <span className="text-[10px]" style={{ color: theme.text, opacity: 0.5 }}>
                      ({loc.reviewCount.toLocaleString()})
                    </span>
                  )}
                </span>
              )}
            </div>

            {loc.note && (
              <p
                className="mt-1.5 text-xs leading-relaxed"
                style={{ color: theme.text, opacity: 0.6 }}
              >
                {loc.note}
              </p>
            )}

            {/* Compact actions — Directions + View Map on the left, Call pinned right */}
            <div className="mt-2.5 flex items-center gap-1.5">
              <a
                href={preview ? undefined : directionsHref}
                target={preview ? undefined : '_blank'}
                rel="noreferrer noopener"
                onClick={(e) => {
                  if (preview) {
                    e.preventDefault();
                    return;
                  }
                  onTap?.(link);
                }}
                className={pill}
                style={{ backgroundColor: theme.accent, color: theme.cardBg }}
              >
                <Navigation className="h-3 w-3" />
                Directions
              </a>
              <a
                href={preview ? undefined : googleHref}
                target={preview ? undefined : '_blank'}
                rel="noreferrer noopener"
                onClick={(e) => {
                  if (preview) {
                    e.preventDefault();
                    return;
                  }
                  onTap?.(link);
                }}
                className={cn(pill, 'border')}
                style={{ borderColor: theme.accent + '66', color: theme.text }}
              >
                <MapIcon className="h-3 w-3" />
                View Map
              </a>
              {loc.phone && (
                <a
                  href={preview ? undefined : `tel:${loc.phone.replace(/\s+/g, '')}`}
                  onClick={(e) => {
                    if (preview) {
                      e.preventDefault();
                      return;
                    }
                    onTap?.(link);
                  }}
                  className={cn(iconBtn, 'ml-auto')}
                  style={{ borderColor: theme.accent + '66', color: theme.text }}
                  aria-label={`Call ${loc.name}`}
                  title="Call"
                >
                  <Phone className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </motion.li>
        );
      })}
    </motion.ul>
  );
}

// ─── Contact / Links list ────────────────────────────────────────────────────
function LinkList({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  const isLight = isLightColor(theme.cardBg);

  // Split items: those with rich embeds render as cards, others stay in the list.
  const embeds: { link: CardLink; embed: ReturnType<typeof detectEmbed> }[] = [];
  const rest: CardLink[] = [];
  for (const link of items) {
    const e = detectEmbed(link.value);
    if (e) embeds.push({ link, embed: e });
    else rest.push(link);
  }

  return (
    <div className="mt-3 space-y-3">
      {embeds.map(({ link, embed }) => embed && (
        <EmbedCard key={link.id} link={link} embed={embed} theme={theme} />
      ))}

      {rest.length === 0 ? null : (
    <motion.ul
      variants={listContainer}
      initial="hidden"
      animate="show"
      className="overflow-hidden rounded-xl border"
      style={{
        backgroundColor: theme.cardBg,
        borderColor: isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)',
      }}
    >
      {rest.map((link, i) => {
        const meta = linkMeta(link.type);
        const Icon = meta.icon;
        const href = preview ? undefined : linkHref(link.type, link.value);
        return (
          <motion.li
            key={link.id}
            variants={listChild}
            className={i > 0 ? 'border-t' : ''}
            style={{
              borderColor: isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)',
            }}
          >
            <a
              href={href}
              target={
                preview
                  ? undefined
                  : link.type === 'email' || link.type === 'phone'
                    ? undefined
                    : '_blank'
              }
              rel="noreferrer noopener"
              onClick={(e) => {
                if (preview) {
                  e.preventDefault();
                  return;
                }
                onTap?.(link);
              }}
              className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-black/[0.03]"
            >
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                style={{ backgroundColor: theme.accent + '22' }}
              >
                <Icon className="h-4 w-4" style={{ color: theme.accent }} />
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="text-sm font-medium tracking-tight"
                  style={{ color: theme.text }}
                >
                  {link.label}
                </p>
                <p
                  className="truncate text-xs"
                  style={{ color: theme.text, opacity: 0.6 }}
                >
                  {link.value}
                </p>
              </div>
              <ChevronRight
                className="h-4 w-4 shrink-0"
                style={{ color: theme.text, opacity: 0.5 }}
              />
            </a>
          </motion.li>
        );
      })}
    </motion.ul>
      )}
    </div>
  );
}

/**
 * Iframe with a soft pulse skeleton until the document loads. Booking pages
 * and media embeds can take seconds — an empty box reads as broken.
 */
function EmbedFrame({
  skeletonClassName,
  className,
  ...props
}: ComponentProps<'iframe'> & { skeletonClassName?: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative">
      {!loaded && (
        <div
          aria-hidden
          className={cn('absolute inset-0 animate-pulse bg-black/10', skeletonClassName)}
        />
      )}
      <iframe
        {...props}
        onLoad={() => setLoaded(true)}
        className={cn(className, 'transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}
      />
    </div>
  );
}

/** Renders a YouTube / Vimeo / Spotify embed inside a Links section. */
function EmbedCard({
  link,
  embed,
  theme,
}: {
  link: CardLink;
  embed: NonNullable<ReturnType<typeof detectEmbed>>;
  theme: ReturnType<typeof getTheme>;
}) {
  const isLight = isLightColor(theme.cardBg);
  const aspect =
    embed.aspect === 'video' ? 'aspect-video' : embed.aspect === 'tall' ? 'aspect-[5/7]' : 'aspect-square';
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{
        backgroundColor: theme.cardBg,
        borderColor: isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)',
      }}
    >
      {link.label && (
        <p
          className="px-4 pt-3 text-sm font-medium tracking-tight"
          style={{ color: theme.text }}
        >
          {link.label}
        </p>
      )}
      <div className={cn(aspect, 'w-full overflow-hidden bg-black [&>div]:h-full')}>
        <EmbedFrame
          src={embed.src}
          title={link.label || embed.kind}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          className="h-full w-full border-0"
          skeletonClassName="bg-white/10"
        />
      </div>
    </div>
  );
}

// ─── Socials: icon-only row ─────────────────────────────────────────────────

function SocialIconRow({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  return (
    <motion.div
      variants={listContainer}
      initial="hidden"
      animate="show"
      className="mt-3 flex flex-wrap gap-2"
    >
      {items.map((link) => {
        const Icon = linkMeta(link.type).icon;
        const href = preview ? undefined : linkHref(link.type, link.value);
        return (
          <motion.a
            key={link.id}
            variants={listChild}
            href={href}
            target={preview ? undefined : '_blank'}
            rel="noreferrer noopener"
            aria-label={link.label || link.type}
            onClick={(e) => {
              if (preview) {
                e.preventDefault();
                return;
              }
              onTap?.(link);
            }}
            className="flex h-12 w-12 items-center justify-center rounded-full border transition-colors"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: theme.accent + '40',
              color: theme.accent,
            }}
          >
            <Icon className="h-5 w-5" />
          </motion.a>
        );
      })}
    </motion.div>
  );
}

// ─── Video carousel with inline playback ────────────────────────────────────
function VideoCarousel({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  const [active, setActive] = useState<CardLink | null>(null);

  return (
    <>
      <div className="mt-3 -mx-5 overflow-x-auto haphe-scroll-x scroll-px-5 snap-x snap-mandatory">
        <motion.div
          variants={listContainer}
          initial="hidden"
          animate="show"
          className="flex gap-3 px-5 pb-2"
        >
          {items.map((link) => {
            const thumb = videoThumbForLink(link);
            return (
              <motion.button
                key={link.id}
                variants={listChild}
                onClick={() => {
                  if (preview) return;
                  onTap?.(link);
                  setActive(link);
                }}
                className="group w-[260px] shrink-0 snap-start text-left"
              >
                <p
                  className="mb-2 line-clamp-1 text-sm font-medium tracking-tight"
                  style={{ color: theme.text }}
                >
                  {link.label || 'Untitled video'}
                </p>
                <div
                  className="relative aspect-video w-full overflow-hidden rounded-lg border"
                  style={{
                    backgroundColor: theme.accent + '22',
                    borderColor: isLightColor(theme.cardBg)
                      ? 'rgba(10,10,10,0.08)'
                      : 'rgba(255,255,255,0.08)',
                  }}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={link.label ?? ''}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="flex h-full w-full items-center justify-center"
                      style={{ color: theme.accent }}
                    >
                      <Play className="h-10 w-10 fill-current opacity-80" />
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                    <Play className="h-12 w-12 text-white fill-current" />
                  </div>
                </div>
                {link.description && (
                  <p
                    className="mt-2 line-clamp-2 text-xs leading-relaxed"
                    style={{ color: theme.text, opacity: 0.7 }}
                  >
                    {link.description}
                  </p>
                )}
              </motion.button>
            );
          })}
        </motion.div>
      </div>
      <CarouselScrollbarStyle />
      <VideoModal video={active} onClose={() => setActive(null)} />
    </>
  );
}

function videoThumbForLink(link: CardLink): string | null {
  // Prefer a stored thumb in metadata, then auto-derive from URL.
  // Guarded access — metadata is freeform Json, so never trust its shape.
  const md = link.metadata;
  if (md && typeof md === 'object' && !Array.isArray(md)) {
    const thumb = (md as Record<string, unknown>).thumb;
    if (typeof thumb === 'string' && thumb) return thumb;
  }
  const yt = youtubeThumb(link.value);
  if (yt) return yt;
  return null;
}

// ─── Gallery ────────────────────────────────────────────────────────────────
/**
 * True on devices with a mouse / trackpad (hover + fine pointer). Those
 * can't swipe a snap carousel, so the gallery becomes a clickable grid.
 * Touch devices keep the swipeable strip.
 */
function usePointerFine(): boolean {
  const query = '(hover: hover) and (pointer: fine)';
  const [fine, setFine] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setFine(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return fine;
}

/**
 * Touch: horizontal snap carousel (swipe). Mouse: two-column grid you
 * click. Both open the same lightbox, which has prev / next and arrow-key
 * navigation. The editor preview always shows the phone version.
 */
function GalleryCarousel({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const pointerFine = usePointerFine();
  const asGrid = pointerFine && !preview;
  const tileBorder = isLightColor(theme.cardBg) ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)';

  if (asGrid) {
    return (
      <>
        <motion.div
          variants={listContainer}
          initial="hidden"
          animate="show"
          className="mt-3 grid grid-cols-2 gap-3"
        >
          {items.map((link, idx) => (
            <motion.button
              key={link.id}
              variants={listChild}
              type="button"
              onClick={() => {
                onTap?.(link);
                setActiveIdx(idx);
              }}
              aria-label={link.description ? `Open photo: ${link.description}` : `Open photo ${idx + 1} of ${items.length}`}
              className="group text-left"
            >
              <div
                className="aspect-square w-full overflow-hidden rounded-xl border transition-shadow group-hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.35)] group-focus-visible:ring-2 group-focus-visible:ring-offset-2"
                style={{ backgroundColor: theme.accent + '15', borderColor: tileBorder }}
              >
                {link.value ? (
                  <img
                    src={link.value}
                    alt={link.description ?? ''}
                    className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-widest"
                    style={{ color: theme.text, opacity: 0.5 }}
                  >
                    No image
                  </div>
                )}
              </div>
              {link.description && (
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed" style={{ color: theme.text, opacity: 0.75 }}>
                  {link.description}
                </p>
              )}
            </motion.button>
          ))}
        </motion.div>
        <MediaLightbox
          items={items}
          index={activeIdx}
          onClose={() => setActiveIdx(null)}
          onNavigate={setActiveIdx}
        />
      </>
    );
  }

  return (
    <>
      <div className="mt-3 -mx-5 overflow-x-auto haphe-scroll-x scroll-px-5 snap-x snap-mandatory">
        <motion.div
          variants={listContainer}
          initial="hidden"
          animate="show"
          className="flex gap-3 px-5 pb-2"
        >
          {items.map((link, idx) => (
            <motion.button
              key={link.id}
              variants={listChild}
              onClick={() => {
                if (preview) return;
                onTap?.(link);
                setActiveIdx(idx);
              }}
              className="w-[280px] shrink-0 snap-start text-left"
            >
              <div
                className="aspect-[4/5] w-full overflow-hidden rounded-xl border"
                style={{
                  backgroundColor: theme.accent + '15',
                  borderColor: isLightColor(theme.cardBg)
                    ? 'rgba(10,10,10,0.08)'
                    : 'rgba(255,255,255,0.08)',
                }}
              >
                {link.value ? (
                  <img
                    src={link.value}
                    alt={link.description ?? ''}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-widest"
                    style={{ color: theme.text, opacity: 0.5 }}
                  >
                    No image
                  </div>
                )}
              </div>
              {link.description && (
                <p
                  className="mt-2 text-xs leading-relaxed"
                  style={{ color: theme.text, opacity: 0.75 }}
                >
                  {link.description}
                </p>
              )}
            </motion.button>
          ))}
        </motion.div>
      </div>
      <CarouselScrollbarStyle />
      <MediaLightbox
        items={items}
        index={activeIdx}
        onClose={() => setActiveIdx(null)}
        onNavigate={setActiveIdx}
      />
    </>
  );
}

// ─── File grid with previews ────────────────────────────────────────────────
function FileGrid({
  items,
  theme,
  preview,
  onTap,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  onTap?: (l: CardLink) => void;
}) {
  return (
    <motion.div
      variants={listContainer}
      initial="hidden"
      animate="show"
      className="mt-3 grid grid-cols-2 gap-3"
    >
      {items.map((link) => {
        const ft = fileTypeFromUrl(link.value);
        const isImage = ft === 'image';
        return (
          <motion.a
            key={link.id}
            variants={listChild}
            href={preview ? undefined : link.value}
            target={preview ? undefined : '_blank'}
            rel="noreferrer noopener"
            onClick={(e) => {
              if (preview) {
                e.preventDefault();
                return;
              }
              onTap?.(link);
            }}
            className="group block overflow-hidden rounded-xl border transition-colors"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: isLightColor(theme.cardBg)
                ? 'rgba(10,10,10,0.08)'
                : 'rgba(255,255,255,0.08)',
            }}
          >
            <div
              className="flex aspect-[5/3] items-center justify-center"
              style={{ backgroundColor: theme.accent + '14' }}
            >
              {isImage && link.value ? (
                <img
                  src={link.value}
                  alt={link.label ?? ''}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                />
              ) : (
                <div className="flex flex-col items-center gap-1" style={{ color: theme.accent }}>
                  <FileText className="h-8 w-8" />
                  <span className="font-mono text-[9px] uppercase tracking-widest">
                    {prettyFileLabel(ft)}
                  </span>
                </div>
              )}
            </div>
            <div className="p-3">
              <p
                className="truncate text-xs font-medium tracking-tight"
                style={{ color: theme.text }}
              >
                {link.label || 'File'}
              </p>
              {link.description && (
                <p
                  className="mt-0.5 line-clamp-2 text-[10px]"
                  style={{ color: theme.text, opacity: 0.6 }}
                >
                  {link.description}
                </p>
              )}
            </div>
          </motion.a>
        );
      })}
    </motion.div>
  );
}

// ─── FAQ (expandable, accent-colored answers) ───────────────────────────────
function FaqList({
  items,
  theme,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
}) {
  return (
    <div
      className="mt-3 overflow-hidden rounded-xl border"
      style={{
        backgroundColor: theme.cardBg,
        borderColor: isLightColor(theme.cardBg)
          ? 'rgba(10,10,10,0.08)'
          : 'rgba(255,255,255,0.08)',
      }}
    >
      {items.map((link, i) => (
        <FaqItem key={link.id} item={link} divider={i > 0} theme={theme} />
      ))}
    </div>
  );
}

function FaqItem({
  item,
  divider,
  theme,
}: {
  item: CardLink;
  divider: boolean;
  theme: ReturnType<typeof getTheme>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={divider ? 'border-t' : ''}
      style={{
        borderColor: isLightColor(theme.cardBg)
          ? 'rgba(10,10,10,0.08)'
          : 'rgba(255,255,255,0.08)',
      }}
    >
      <button
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-black/[0.03]"
      >
        <span
          className="text-sm font-medium tracking-tight"
          style={{ color: theme.text }}
        >
          {item.label || 'Question'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          style={{ color: theme.text, opacity: 0.6 }}
        />
      </button>
      {open && item.description && (
        <p
          className="px-4 pb-3.5 text-sm leading-relaxed"
          style={{ color: theme.accent }}
        >
          {item.description}
        </p>
      )}
    </div>
  );
}

// ─── Products grid (tap for details, add-to-order) ───────────────────────────
function ProductsSection({
  items,
  theme,
  preview,
  canOrder,
  shopOn,
}: {
  items: CardLink[];
  theme: ReturnType<typeof getTheme>;
  preview?: boolean;
  canOrder: boolean;
  shopOn: boolean;
}) {
  const cart = useCart();
  const [active, setActive] = useState<Product | null>(null);
  const isLight = isLightColor(theme.cardBg);
  const border = isLight ? 'rgba(10,10,10,0.08)' : 'rgba(255,255,255,0.08)';
  const products = items.map(productFromLink).filter((p) => p.name);
  if (products.length === 0) return null;

  function onAdd(p: Product) {
    if (preview) {
      setActive(p);
      return;
    }
    // Products with options need a choice — open the sheet instead.
    if (p.options.some((o) => o.values.length > 0)) setActive(p);
    else cart?.add(p);
  }

  return (
    <>
      {preview && !shopOn && (
        <p
          className="mt-3 rounded-lg border border-dashed px-3 py-2 text-[11px]"
          style={{ borderColor: theme.accent + '80', color: theme.text, opacity: 0.8 }}
        >
          Shop is off — visitors won't see this section until you turn it on under Shop.
        </p>
      )}
      <motion.ul
        variants={listContainer}
        initial="hidden"
        animate="show"
        className="mt-3 grid grid-cols-2 gap-3"
      >
        {products.map((p) => {
          const qty = cart?.qtyOf(p.id) ?? 0;
          const soldOut = !p.available;
          const hasOptions = p.options.some((o) => o.values.length > 0);
          return (
            <motion.li
              key={p.id}
              variants={listChild}
              className={cn(
                'flex flex-col overflow-hidden rounded-xl border',
                !p.published && 'opacity-60',
              )}
              style={{ backgroundColor: theme.cardBg, borderColor: border }}
            >
              <button
                type="button"
                onClick={() => setActive(p)}
                className="relative aspect-square w-full overflow-hidden text-left"
                style={{ backgroundColor: theme.accent + '14' }}
                aria-label={`${p.name} — details`}
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.name}
                    loading="lazy"
                    className={cn('h-full w-full object-cover', soldOut && 'opacity-60 grayscale')}
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center"
                    style={{ color: theme.accent }}
                  >
                    <ShoppingBag className="h-8 w-8 opacity-70" />
                  </div>
                )}
                {(soldOut || p.badges.length > 0 || !p.published) && (
                  <div className="absolute left-2 top-2 flex max-w-[calc(100%-1rem)] flex-wrap gap-1">
                    {!p.published && (
                      <span
                        className="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                        style={{ backgroundColor: theme.text, color: theme.cardBg }}
                      >
                        Hidden
                      </span>
                    )}
                    {soldOut && (
                      <span
                        className="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                        style={{ backgroundColor: theme.text, color: theme.cardBg }}
                      >
                        Sold out
                      </span>
                    )}
                    {p.badges.map((b) => (
                      <span
                        key={b}
                        className="rounded-full px-2 py-0.5 font-mono text-[9px] font-medium uppercase tracking-widest"
                        style={{ backgroundColor: theme.accent, color: theme.cardBg }}
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                )}
              </button>
              <div className="flex flex-1 flex-col p-3">
                <button type="button" onClick={() => setActive(p)} className="text-left">
                  <p
                    className="line-clamp-2 text-sm font-medium leading-snug tracking-tight"
                    style={{ color: theme.text }}
                  >
                    {p.name}
                  </p>
                  {p.description && (
                    <p
                      className="mt-1 line-clamp-2 text-[11px] leading-snug"
                      style={{ color: theme.text, opacity: 0.65 }}
                    >
                      {p.description}
                    </p>
                  )}
                </button>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                  <span
                    className="text-sm font-medium tabular-nums"
                    style={{ color: theme.text }}
                  >
                    {formatMoney(p.priceCents, p.currency)}
                  </span>
                  {canOrder && !soldOut && (
                    qty === 0 || hasOptions ? (
                      <button
                        type="button"
                        onClick={() => onAdd(p)}
                        aria-label={`Add ${p.name} to order`}
                        className="relative flex h-8 min-w-8 items-center justify-center rounded-full px-2 transition-transform active:scale-95"
                        style={{ backgroundColor: theme.accent, color: theme.cardBg }}
                      >
                        <Plus className="h-4 w-4" />
                        {qty > 0 && (
                          <span className="ml-1 font-mono text-[10px] font-medium tabular-nums">{qty}</span>
                        )}
                      </button>
                    ) : (
                      <div
                        className="flex h-8 items-center rounded-full border"
                        style={{ borderColor: theme.accent + '80', color: theme.text }}
                      >
                        <button
                          type="button"
                          onClick={() => !preview && cart?.setQty(p.id, qty - 1)}
                          aria-label="Remove one"
                          className="flex h-8 w-7 items-center justify-center"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-4 text-center text-xs font-medium tabular-nums">{qty}</span>
                        <button
                          type="button"
                          onClick={() => !preview && cart?.add(p)}
                          aria-label="Add one"
                          className="flex h-8 w-7 items-center justify-center"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </motion.li>
          );
        })}
      </motion.ul>
      <ProductSheet
        product={active}
        theme={theme}
        canOrder={canOrder}
        preview={preview}
        onClose={() => setActive(null)}
      />
    </>
  );
}

// Helper component to hide scrollbar in horizontal carousels (one shared style)
function CarouselScrollbarStyle() {
  return (
    <style>{`
      .haphe-scroll-x { scrollbar-width: none; -ms-overflow-style: none; }
      .haphe-scroll-x::-webkit-scrollbar { display: none; }
    `}</style>
  );
}

// Re-export to keep video helpers tree-shakeable from PublicCard route
export { youtubeEmbed, vimeoEmbed, isVimeo, isUploadedVideo };
